import { useSelector } from "react-redux";
import { LogicalOperator } from "@financial-advisory/data-access/api-types";
import {
  selectActiveClause,
  selectActivePaneId,
  useGetEvaluationExamplesQuery,
  useReviewProcessedClauseMutation,
  useUiSwitcher,
} from "@financial-advisory/data-access/financial-advisory-api-service";
import { SecondaryButton } from "@financial-advisory/ui/components/buttons";
import {
  IconCheck,
  IconEdit,
  IconMessageCircle,
  IconX,
} from "@tabler/icons-react";
import { Tooltip } from "antd";
import { LogicalOperatorPill } from "@unlikelyai-magic/shared/components";
import { useModal } from "@unlikelyai-magic/ui/modals";
import { cn } from "@unlikelyai-magic/utils";
import { useRegulations } from "../providers/RegulationsProvider";
import { QueryModal } from "./QueryModal";

export interface GraphHeaderProps {
  onEditTree?: () => void;
}

export const GraphHeader = ({ onEditTree }: GraphHeaderProps) => {
  const { openModal } = useModal();
  const activePaneId = useSelector(selectActivePaneId);
  const activeClause = useSelector(selectActiveClause);
  const isUncheck = useUiSwitcher();
  const { regulation } = useRegulations();
  const [reviewClause, { isLoading: isReviewing }] =
    useReviewProcessedClauseMutation();

  const openQueryModal = () => {
    openModal(<QueryModal />);
  };

  const handleReview = (reviewStatus: "APPROVED" | "REJECTED") => {
    if (!activeClause?.recordId || !regulation?.id) return;
    reviewClause({
      regulationId: regulation.id,
      processedClauseId: activeClause.recordId,
      reviewStatus,
    });
  };

  // Pre-fetch evaluation examples when active pane ID is set
  useGetEvaluationExamplesQuery(
    { pane_id: activePaneId ?? "" },
    { skip: !activePaneId }
  );

  const reviewStatus = activeClause?.reviewStatus;

  return (
    <div className={cn("bg-white flex flex-col gap-2 overflow-y-auto")}>
      <div className="sticky top-0 bg-white flex flex-row items-center justify-between gap-2 border-b border-gray-200 px-3 min-h-[3.375rem]">
        <div className="flex flex-row gap-2">
          <div className="flex flex-row items-center gap-2">
            <LogicalOperatorPill operator={LogicalOperator.AND} />
            <span className="text-sm text-gray-400">
              All conditions must be true
            </span>
          </div>
          <div className="flex flex-row items-center gap-2">
            <LogicalOperatorPill operator={LogicalOperator.OR} />
            <span className="text-sm text-gray-400">
              Any condition can be true
            </span>
          </div>
          <div className="flex flex-row items-center gap-2">
            <LogicalOperatorPill operator={LogicalOperator.NOT} />
            <span className="text-sm text-gray-400">Negates the condition</span>
          </div>
        </div>
        <div className="flex flex-row items-center justify-end gap-2">
          {isUncheck && onEditTree && (
            <SecondaryButton
              icon={<IconEdit size={16} />}
              onClick={onEditTree}
              title="Edit tree (add/remove nodes, change connectors and text)"
            >
              Edit tree
            </SecondaryButton>
          )}
          {isUncheck && activeClause && (
            <>
              <SecondaryButton
                icon={<IconCheck size={16} />}
                onClick={() => handleReview("APPROVED")}
                disabled={isReviewing}
                title=""
                style={
                  reviewStatus === "APPROVED"
                    ? { backgroundColor: "#BBF7D0", color: "#166534" }
                    : {}
                }
              >
                Approve
              </SecondaryButton>
              <SecondaryButton
                icon={<IconX size={16} />}
                onClick={() => handleReview("REJECTED")}
                disabled={isReviewing}
                title=""
                style={
                  reviewStatus === "REJECTED"
                    ? { backgroundColor: "#FECACA", color: "#991B1B" }
                    : {}
                }
              >
                Reject
              </SecondaryButton>
            </>
          )}
          <Tooltip
            title={!activePaneId ? "Click a definition to query" : ""}
            mouseEnterDelay={0}
            placement="bottom"
          >
            <span>
              <SecondaryButton
                icon={<IconMessageCircle />}
                onClick={openQueryModal}
                disabled={!activePaneId}
                title=""
                style={
                  activePaneId
                    ? {
                        backgroundColor: "#BFDBFE", // blue-200
                        color: "#1E40AF", // blue-800
                      }
                    : {
                        pointerEvents: "none",
                      }
                }
              >
                Query
              </SecondaryButton>
            </span>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
